import _ from 'lodash';
import type { EditQuoteState } from '../../views/edit-quote/types';
import type { UnderwriteQuoteState } from '../../views/underwriter/types';
import type { RaterResponse } from '../../../../../agencies/quotes/PrimeX/quoteDetails/hooks/useGetQuotePageDetails';

/**
 * Extracts editable coverage-category and rate-modifier-category selections
 * directly from a RaterResponse. Used to seed the recalculate payload when
 * re-seeding from policy data rather than a quote.
 */
export function pullCategoryCustomizationsFromRaterResponse(
  raterResponse: RaterResponse | undefined
): UnderwriteQuoteState['categoryCustomizations'] {
  const rateModifiersCustomizations =
    raterResponse?.rateModifierCategories?.reduce((acc, mod) => {
      if (!mod.isEditable) return acc;
      return { ...acc, [mod.key]: { select: mod.isSelected } };
    }, {} as Record<string, { select: boolean }>);

  const categoryCustomizations = raterResponse?.coverageCategories?.reduce(
    (acc, group) => {
      if (!group.isEditable) return acc;
      return { ...acc, [group.key]: { select: group.isSelected } };
    },
    {} as Record<string, { select: boolean }>
  );

  return {
    ...(!_.isEmpty(rateModifiersCustomizations) && {
      rateModifierCategoriesCustomizations: rateModifiersCustomizations,
    }),
    ...(!_.isEmpty(categoryCustomizations) && {
      coverageCategoriesCustomizations: categoryCustomizations,
    }),
  };
}

export function pullCategoryCustomizationsFromQuote(
  quote: EditQuoteState['quote'] | undefined
): UnderwriteQuoteState['categoryCustomizations'] {
  return pullCategoryCustomizationsFromRaterResponse(
    quote?.quote.raterResponse
  );
}

const mergeCategoryCustomizationsIntoState = <
  T extends EditQuoteState & Partial<UnderwriteQuoteState>
>(
  data: T
) => {
  const customizations = pullCategoryCustomizationsFromQuote(data.quote);
  return {
    ...data,
    categoryCustomizations: customizations,
  };
};

export default mergeCategoryCustomizationsIntoState;
